import { useState, useEffect } from 'react';
import { catalogService, CatalogType, CatalogItem } from '../../services/catalog.service';

interface CatalogTab {
  type: CatalogType;
  label: string;
  icon: string;
  hasKind: boolean;
  kindColumn?: string;
  fields: {
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'select';
    options?: { value: string; label: string }[];
    required?: boolean;
  }[];
}

const CATALOG_TABS: CatalogTab[] = [
  {
    type: 'concepts',
    label: 'Conceptos',
    icon: '💡',
    hasKind: true,
    kindColumn: 'entry_type',
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'entry_type',
        label: 'Tipo',
        type: 'select',
        required: true,
        options: [
          { value: 'I', label: 'Ingreso' },
          { value: 'E', label: 'Egreso' },
          { value: 'B', label: 'Ambos' },
        ],
      },
    ],
  },
  {
    type: 'movement_types',
    label: 'Tipos de Movimiento',
    icon: '🔀',
    hasKind: true,
    kindColumn: 'movement_kind',
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'movement_kind',
        label: 'Tipo',
        type: 'select',
        required: true,
        options: [
          { value: 'I', label: 'Ingreso' },
          { value: 'E', label: 'Egreso' },
          { value: 'B', label: 'Ambos' },
        ],
      },
    ],
  },
  {
    type: 'movement_details',
    label: 'Detalles de Movimiento',
    icon: '📋',
    hasKind: true,
    kindColumn: 'movement_kind',
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'movement_kind',
        label: 'Tipo',
        type: 'select',
        required: true,
        options: [
          { value: 'I', label: 'Ingreso' },
          { value: 'E', label: 'Egreso' },
          { value: 'B', label: 'Ambos' },
        ],
      },
    ],
  },
  {
    type: 'departments',
    label: 'Departamentos',
    icon: '🏢',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
    ],
  },
  {
    type: 'financial_locations',
    label: 'Ubicaciones Físicas',
    icon: '📍',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
      {
        name: 'location_type',
        label: 'Tipo de ubicación',
        type: 'select',
        required: true,
        options: [
          { value: 'drawer', label: 'Cajón' },
          { value: 'safe', label: 'Caja fuerte' },
          { value: 'cash_register', label: 'Caja registradora' },
          { value: 'locker', label: 'Casillero' },
          { value: 'other', label: 'Otro' },
        ],
      },
    ],
  },
  {
    type: 'currency_types',
    label: 'Tipos de Moneda',
    icon: '💵',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre', type: 'text', required: true },
      { name: 'code', label: 'Código (ej: cash, check)', type: 'text', required: true },
      { name: 'description', label: 'Descripción', type: 'textarea' },
    ],
  },
  {
    type: 'suppliers',
    label: 'Proveedores',
    icon: '🏭',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre / Razón social', type: 'text', required: true },
      { name: 'rfc', label: 'RFC', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'phone', label: 'Teléfono', type: 'text' },
      { name: 'contact_name', label: 'Persona de contacto', type: 'text' },
      { name: 'address', label: 'Dirección', type: 'textarea' },
      { name: 'category', label: 'Categoría', type: 'text' },
      { name: 'notes', label: 'Notas', type: 'textarea' },
    ],
  },
  {
    type: 'contractors',
    label: 'Contratistas',
    icon: '🔨',
    hasKind: false,
    fields: [
      { name: 'name', label: 'Nombre completo', type: 'text', required: true },
      { name: 'rfc', label: 'RFC', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'phone', label: 'Teléfono', type: 'text' },
      { name: 'specialty', label: 'Especialidad', type: 'text' },
      { name: 'contact_name', label: 'Persona de contacto', type: 'text' },
      { name: 'address', label: 'Dirección', type: 'textarea' },
      { name: 'notes', label: 'Notas', type: 'textarea' },
    ],
  },
];

export function CatalogsPage() {
  const [activeTab, setActiveTab] = useState<CatalogType>('concepts');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  const currentTab = CATALOG_TABS.find(t => t.type === activeTab)!;

  useEffect(() => {
    loadItems();
  }, [activeTab]);

  const loadItems = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await catalogService.list(activeTab, { activeOnly: false });
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar catálogo');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingItem(null);
    const initial: Record<string, any> = {};
    currentTab.fields.forEach(f => {
      initial[f.name] = f.type === 'select' ? (f.options?.[0]?.value || '') : '';
    });
    setFormData(initial);
    setShowModal(true);
  };

  const handleOpenEdit = (item: CatalogItem) => {
    setEditingItem(item);
    const initial: Record<string, any> = {};
    currentTab.fields.forEach(f => {
      initial[f.name] = item[f.name] || '';
    });
    setFormData(initial);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({});
    setError('');
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (editingItem) {
        await catalogService.update(activeTab, editingItem.id, formData);
        setSuccess('Registro actualizado');
      } else {
        await catalogService.create(activeTab, formData);
        setSuccess('Registro creado');
      }
      handleCloseModal();
      await loadItems();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (item: CatalogItem) => {
    try {
      await catalogService.toggleActive(activeTab, item.id);
      await loadItems();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const getKindLabel = (item: CatalogItem) => {
    if (!currentTab.hasKind || !currentTab.kindColumn) return null;
    const value = item[currentTab.kindColumn];
    const labels: Record<string, { label: string; badge: string }> = {
      I: { label: '📥 Ingreso', badge: 'badge-success' },
      E: { label: '📤 Egreso', badge: 'badge-error' },
      B: { label: '🔄 Ambos', badge: 'badge-neutral' },
    };
    return labels[value] || { label: value, badge: 'badge-neutral' };
  };

  return (
    <div className="page-container">
      {/* Encabezado */}
      <div className="page-header-bar">
        <div>
          <h1>Catálogos</h1>
          <p className="subtitle">Administración de catálogos del sistema</p>
        </div>
        <div className="actions">
          <button onClick={loadItems} className="btn btn-ghost">🔄 Actualizar</button>
          <button onClick={handleOpenCreate} className="btn btn-primary">
            + Nuevo {currentTab.label.slice(0, -1)}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-bar">
        {CATALOG_TABS.map(tab => (
          <button
            key={tab.type}
            onClick={() => setActiveTab(tab.type)}
            className={`tab-button ${activeTab === tab.type ? 'active' : ''}`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Error / Success */}
      {error && !showModal && (
        <div className="info-panel error">
          <span className="info-panel-icon">❌</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{error}</p>
          </div>
        </div>
      )}
      {success && (
        <div className="info-panel success">
          <span className="info-panel-icon">✅</span>
          <div className="info-panel-body">
            <p className="info-panel-text">{success}</p>
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">Cargando {currentTab.label.toLowerCase()}...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">{currentTab.icon}</div>
          <h3 className="empty-state-title">Sin registros</h3>
          <p className="empty-state-text">
            No hay {currentTab.label.toLowerCase()} registrados aún
          </p>
          <button onClick={handleOpenCreate} className="btn btn-primary">
            + Crear primer registro
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Descripción</th>
                {currentTab.hasKind && <th>Tipo</th>}
                {activeTab === 'currency_types' && <th>Código</th>}
                {activeTab === 'financial_locations' && <th>Tipo</th>}
                <th className="col-center">Estado</th>
                <th className="col-actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const kindInfo = getKindLabel(item);
                return (
                  <tr key={item.id}>
                    <td style={{ fontWeight: 600 }}>{item.name}</td>
                    <td style={{ color: 'var(--gray)', fontSize: '13px' }}>
                      {item.description || '—'}
                    </td>
                    {currentTab.hasKind && (
                      <td>
                        {kindInfo && (
                          <span className={`badge ${kindInfo.badge}`}>
                            {kindInfo.label}
                          </span>
                        )}
                      </td>
                    )}
                    {activeTab === 'currency_types' && (
                      <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {item.code}
                      </td>
                    )}
                    {activeTab === 'financial_locations' && (
                      <td>
                        <span className="badge badge-neutral">
                          {item.location_type}
                        </span>
                      </td>
                    )}
                    <td className="col-center">
                      <span className={`badge ${item.is_active ? 'badge-success' : 'badge-neutral'}`}>
                        {item.is_active ? '✅ Activo' : '⏸️ Inactivo'}
                      </span>
                    </td>
                    <td className="col-actions">
                      <div className="action-buttons-group">
                        <button
                          onClick={() => handleOpenEdit(item)}
                          className="btn-action-edit"
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleToggleActive(item)}
                          className="btn-action-toggle"
                        >
                          {item.is_active ? '⏸️' : '▶️'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear/editar */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingItem ? '✏️ Editar' : '➕ Nuevo'} {currentTab.label.slice(0, -1)}
              </h3>
              <button onClick={handleCloseModal} className="modal-close">✕</button>
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body">
                {currentTab.fields.map(field => (
                  <div className="form-row" key={field.name}>
                    <div className="form-col">
                      <label className={`form-label ${field.required ? 'required' : ''}`}>
                        {field.label}
                      </label>
                      {field.type === 'text' && (
                        <input
                          type="text"
                          className="form-input"
                          required={field.required}
                          value={formData[field.name] || ''}
                          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        />
                      )}
                      {field.type === 'textarea' && (
                        <textarea
                          className="form-textarea"
                          rows={2}
                          required={field.required}
                          value={formData[field.name] || ''}
                          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        />
                      )}
                      {field.type === 'select' && (
                        <select
                          className="form-select"
                          required={field.required}
                          value={formData[field.name] || ''}
                          onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        >
                          <option value="">Seleccionar...</option>
                          {field.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                ))}

                {error && (
                  <div className="info-panel error" style={{ marginTop: '16px' }}>
                    <span className="info-panel-icon">❌</span>
                    <div className="info-panel-body">
                      <p className="info-panel-text">{error}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" onClick={handleCloseModal} className="btn btn-ghost">
                  Cancelar
                </button>
                <button type="submit" disabled={isSaving} className="btn btn-primary">
                  {isSaving ? 'Guardando...' : editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}